import { Fragment, useMemo, useState } from 'react'
import { LightningIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useListCopy } from 'app/hooks/useListCopy'
import { cn } from 'app/lib/utils'
import {
  type TriggerKind,
  type TriggerPickerRow,
  type TriggerPresetData,
  triggerPickerRows,
  triggerPickerSearch,
} from 'app/lib/workflows/triggerCatalog'
import { InfoHint } from './fields'
import { InspectorShell } from './InspectorShell'
import { SECTION_TITLE_CLASS } from './Section'

/** A chosen trigger: a base object event, optionally with a named moment's prefill (from a row like
 *  "Missed call"). `data` is merged into the new trigger node and stays fully editable. `summary`
 *  keeps the moment's own subtitle on the node card instead of the base event's. */
export type TriggerChoice = { kind: TriggerKind; label: string; summary?: string; data?: TriggerPresetData }

function Row({ row, onSelect }: { row: TriggerPickerRow; onSelect: (choice: TriggerChoice) => void }) {
  return (
    <div className="group flex items-center gap-1">
      <Button
        variant="ghost"
        size="row"
        onClick={() => onSelect({ kind: row.kind, label: row.label, summary: row.detail, data: row.data })}
        className="min-w-0 flex-1"
      >
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-surface-muted text-subtle">
          <row.icon size={15} weight="bold" className="size-[15px]" />
        </span>
        <span className="truncate text-sm text-default">{row.label}</span>
        {row.status === `backend` && (
          <span className="ml-auto shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold tracking-wide text-amber-700 uppercase">
            Needs backend
          </span>
        )}
      </Button>
      {/* Outside the button so the row's accessible name stays the label alone. */}
      <span className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <InfoHint text={row.detail} />
      </span>
    </div>
  )
}

/** The trigger picker, as inspector content (design language: one frame per decision). Search on
 *  top, then one list grouped by object, a group with named moments
 *  splits under "Ready-made" and "Start from scratch" captions. Only object groups get a rule, so
 *  the two levels never look alike. */
export function TriggerPicker({
  onSelect,
  onClose,
}: {
  onSelect: (choice: TriggerChoice) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState(``)
  const copy = useListCopy()
  const paperworkEnabled = !!useFeatureToggle(`paperwork`)
  const allRows = useMemo(
    () => triggerPickerRows(copy).filter((row) => paperworkEnabled || row.kind !== `paperwork_completed`),
    [copy, paperworkEnabled],
  )
  const rows = triggerPickerSearch(allRows, query)

  return (
    <InspectorShell icon={LightningIcon} title="Trigger" subtitle="What starts this workflow" onClose={onClose}>
      <div className="space-y-3">
        <label className="relative flex items-center">
          <MagnifyingGlassIcon size={14} weight="bold" className="pointer-events-none absolute left-2.5 text-minimal" />
          <input
            autoFocus
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search triggers"
            aria-label="Search triggers"
            className="w-full rounded-md border border-default bg-surface-subtle/60 py-1.5 pr-2 pl-8 text-sm text-default placeholder:text-minimal focus:border-strong focus:bg-surface focus:outline-none"
          />
        </label>
        {rows.length === 0 ? (
          <div className="px-2 py-6 text-center text-xs text-subtle">No trigger matches</div>
        ) : (
          <div className="-mx-2 space-y-0.5">
            {rows.map((row, index) => {
              const previous = rows[index - 1]
              const heading = row.group !== previous?.group ? row.group : undefined
              const startsReadyMade = heading && row.named
              const startsScratch = !heading && previous?.named && !row.named
              const subheading = startsReadyMade ? `Ready-made` : startsScratch ? `Start from scratch` : undefined
              return (
                <Fragment key={row.id}>
                  {heading && (
                    <div
                      className={cn(
                        `px-2 pb-1`,
                        index > 0 ? `mt-3 border-t border-default pt-3` : `pt-1`,
                        SECTION_TITLE_CLASS,
                      )}
                    >
                      {heading}
                    </div>
                  )}
                  {subheading && <div className="px-2 pt-2 pb-0.5 text-caption-sm text-minimal">{subheading}</div>}
                  <Row row={row} onSelect={onSelect} />
                </Fragment>
              )
            })}
          </div>
        )}
      </div>
    </InspectorShell>
  )
}
